import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, Clock, ChevronDown, ChevronUp, ChevronRight, 
  Award, Users, AlertCircle, Edit3, DollarSign, Trash2, Plus, Ban, Check, Loader2,
  X, Phone, MapPin, Star, Mail, Search, Car, Shield, Camera, MessageCircle, Gift, Sparkles, Link2, Copy
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { useAuth } from "../context";
import { PACKAGES, ADDONS, VEHICLE_TYPES } from "../context/BookingContext";
import { API, TIME_SLOTS, ADMIN_TIMES, getStatusColor, formatStatus } from "../lib/constants";

const AdminPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState("list"); // list, calendar, or clients
  const [calendarData, setCalendarData] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("");
  
  // Client List state
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientBookings, setClientBookings] = useState([]);
  const [clientNotes, setClientNotes] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  
  // Client edit state
  const [editingClient, setEditingClient] = useState(false);
  const [editClientData, setEditClientData] = useState({});
  const [showGiveReward, setShowGiveReward] = useState(false);
  const [rewardData, setRewardData] = useState({ type: 'loyalty_points', value: 1, reason: '' });
  
  // Photo upload state
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef(null);
  const [photoUploadType, setPhotoUploadType] = useState("before"); // before or after
  
  // Booking edit state
  const [editingBookingServices, setEditingBookingServices] = useState(false);
  const [editBookingData, setEditBookingData] = useState({});
  const [manualDiscount, setManualDiscount] = useState({ type: 'percentage', value: 0, reason: '' });
  
  // Payment recording state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payments, setPayments] = useState([{ method: 'cash', amount: 0, notes: '' }]);
  
  // Sales Report state
  const [salesReport, setSalesReport] = useState(null);
  const [salesPeriod, setSalesPeriod] = useState("week");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);
  
  // Subscribers & Email state
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState({ total: 0, with_profile: 0, guests: 0 });
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailData, setEmailData] = useState({ subject: '', content: '', recipient_type: 'all', test_mode: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Blocked slots state
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockDate, setBlockDate] = useState(null);
  const [blockStartTime, setBlockStartTime] = useState("");
  const [blockEndTime, setBlockEndTime] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockWholeDay, setBlockWholeDay] = useState(false);
  
  // Add booking modal state
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [clientSearchForBooking, setClientSearchForBooking] = useState("");
  const [selectedClientForBooking, setSelectedClientForBooking] = useState(null);
  const [newBookingData, setNewBookingData] = useState({
    customer_name: "", phone: "", email: "", address: "",
    vehicle_type: "small", make_model: "", package: "xpress_clean",
    addons: [], preferred_date: "", preferred_time: "", special_instructions: "", admin_notes: "",
    is_custom_package: false, custom_package_name: "", custom_price: "",
    custom_addons: []
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Navigation state for client->booking view
  const [previousView, setPreviousView] = useState(null);
  const [previousClient, setPreviousClient] = useState(null);

  const admin_times = ADMIN_TIMES;

  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(true);
        fetchData();
      } catch (error) {
        setIsAdmin(false);
        setLoading(false);
      }
    };
    checkAdmin();
  }, [token]);

  const fetchData = async () => {
    try {
      const [bookingsRes, statsRes, calendarRes] = await Promise.all([
        axios.get(`${API}/admin/bookings${statusFilter ? `?status=${statusFilter}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/calendar?month=${currentMonth}&year=${currentYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setBookings(bookingsRes.data.bookings);
      setStats(statsRes.data);
      setCalendarData(calendarRes.data.bookings_by_date);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      setLoading(false);
    }
  };
  
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [statusFilter, currentMonth, currentYear, isAdmin]);
  
  useEffect(() => {
    if (isAdmin && view === "clients") {
      fetchClients();
    }
  }, [isAdmin, view]);

  // Fetch sales report when view changes to sales or period changes
  const fetchSalesReport = async () => {
    setLoadingSales(true);
    try {
      let url = `${API}/admin/sales-report?period=${salesPeriod}`;
      if (salesPeriod === "custom" && customStartDate && customEndDate) {
        url += `&start_date=${format(customStartDate, "yyyy-MM-dd")}&end_date=${format(customEndDate, "yyyy-MM-dd")}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesReport(response.data);
    } catch (error) {
      console.error("Failed to fetch sales report:", error);
      toast.error("Failed to load sales report");
    } finally {
      setLoadingSales(false);
    }
  };

  useEffect(() => {
    if (isAdmin && view === "sales") {
      fetchSalesReport();
    }
  }, [isAdmin, view, salesPeriod, customStartDate, customEndDate]);

  // Fetch subscribers
  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data.subscribers || []);
      setSubscriberStats({
        total: response.data.total || 0,
        with_profile: response.data.with_profile || 0,
        guests: response.data.guests || 0
      });
    } catch (error) {
      console.error("Failed to fetch subscribers:", error);
    }
  };

  useEffect(() => {
    if (isAdmin && view === "subscribers") {
      fetchSubscribers();
    }
  }, [isAdmin, view]);

  // Send bulk email
  const handleSendBulkEmail = async () => {
    if (!emailData.subject || !emailData.content) {
      toast.error("Please fill in subject and content");
      return;
    }
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/send-bulk-email`, {
        subject: emailData.subject,
        html_content: emailData.content,
        recipient_type: emailData.recipient_type,
        test_mode: emailData.test_mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowEmailComposer(false);
        setEmailData({ subject: '', content: '', recipient_type: 'all', test_mode: false });
      } else {
        toast.error(response.data.message || "Failed to send email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Fetch blocked slots
  const fetchBlockedSlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/blocked-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedSlots(response.data.blocked_slots || []);
    } catch (error) {
      console.error("Failed to fetch blocked slots:", error);
    }
  };

  useEffect(() => {
    if (isAdmin && (view === "schedule" || view === "calendar")) {
      fetchBlockedSlots();
    }
  }, [isAdmin, view]);

  const handleBlockSlot = async () => {
    if (!blockDate) {
      toast.error("Please select a date to block");
      return;
    }
    
    try {
      const dateStr = format(blockDate, "yyyy-MM-dd");
      
      if (blockWholeDay) {
        // Block whole day
        await axios.post(`${API}/admin/blocked-slots`, {
          date: dateStr,
          time: null,
          reason: blockReason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Day blocked successfully");
      } else if (blockStartTime && blockEndTime) {
        // Block time range - create multiple slots
        const startIndex = TIME_SLOTS.indexOf(blockStartTime);
        const endIndex = TIME_SLOTS.indexOf(blockEndTime);
        
        if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
          toast.error("Please select valid start and end times");
          return;
        }
        
        // Block each time slot in the range
        const slotsToBlock = TIME_SLOTS.slice(startIndex, endIndex + 1);
        for (const time of slotsToBlock) {
          await axios.post(`${API}/admin/blocked-slots`, {
            date: dateStr,
            time: time,
            reason: blockReason
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        toast.success(`${slotsToBlock.length} time slots blocked (${blockStartTime} - ${blockEndTime})`);
      } else if (blockStartTime) {
        // Block single time slot
        await axios.post(`${API}/admin/blocked-slots`, {
          date: dateStr,
          time: blockStartTime,
          reason: blockReason
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Time slot blocked successfully");
      } else {
        toast.error("Please select a time or check 'Block entire day'");
        return;
      }
      
      setBlockDate(null);
      setBlockStartTime("");
      setBlockEndTime("");
      setBlockReason("");
      setBlockWholeDay(false);
      fetchBlockedSlots();
    } catch (error) {
      toast.error("Failed to block slot");
    }
  };

  const handleUnblockSlot = async (date, time) => {
    try {
      await axios.delete(`${API}/admin/blocked-slots?date=${date}${time ? `&time=${encodeURIComponent(time)}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Slot unblocked");
      fetchBlockedSlots();
    } catch (error) {
      toast.error("Failed to unblock slot");
    }
  };

  // Admin create booking
  const handleAdminCreateBooking = async () => {
    if (!newBookingData.customer_name || !newBookingData.phone || !newBookingData.preferred_date || !newBookingData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate custom package fields
    if (newBookingData.is_custom_package) {
      if (!newBookingData.custom_package_name || !newBookingData.custom_price) {
        toast.error("Please enter custom package name and price");
        return;
      }
    }
    
    // Validate custom add-ons (remove empty ones, validate prices)
    const validCustomAddons = newBookingData.custom_addons.filter(a => a.name && a.price);
    
    try {
      const bookingPayload = {
        ...newBookingData,
        user_id: selectedClientForBooking?.id || null,
        // For custom packages, use the custom name and price
        package: newBookingData.is_custom_package ? newBookingData.custom_package_name : newBookingData.package,
        custom_price: newBookingData.is_custom_package ? parseFloat(newBookingData.custom_price) : null,
        custom_addons: validCustomAddons.map(a => ({ name: a.name, price: parseFloat(a.price) }))
      };
      
      await axios.post(`${API}/admin/bookings/create`, bookingPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking created successfully!");
      setShowAddBookingModal(false);
      setSelectedClientForBooking(null);
      setNewBookingData({
        customer_name: "", phone: "", email: "", address: "",
        vehicle_type: "small", make_model: "", package: "xpress_clean",
        addons: [], preferred_date: "", preferred_time: "", special_instructions: "", admin_notes: "",
        is_custom_package: false, custom_package_name: "", custom_price: "",
        custom_addons: []
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create booking");
    }
  };

  // Create new client account from admin
  const handleCreateClientAccount = async () => {
    if (!newBookingData.customer_name || !newBookingData.email || !newBookingData.phone) {
      toast.error("Name, email, and phone are required to create an account");
      return;
    }
    setCreatingClient(true);
    try {
      const response = await axios.post(`${API}/admin/clients/create`, {
        name: newBookingData.customer_name,
        email: newBookingData.email,
        phone: newBookingData.phone,
        address: newBookingData.address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setSelectedClientForBooking(response.data.user);
      fetchClients(); // Refresh client list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client account");
    } finally {
      setCreatingClient(false);
    }
  };

  // Send invoice to client
  const handleSendInvoice = async (bookingId) => {
    setSendingInvoice(true);
    try {
      const response = await axios.post(`${API}/admin/bookings/${bookingId}/send-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSendingInvoice(false);
    }
  };

  // Search clients for booking
  const filteredClientsForBooking = clients.filter(client =>
    clientSearchForBooking.length > 0 && (
      client.name?.toLowerCase().includes(clientSearchForBooking.toLowerCase()) ||
      client.email?.toLowerCase().includes(clientSearchForBooking.toLowerCase()) ||
      client.phone?.includes(clientSearchForBooking)
    )
  );

  const openClientDetail = async (client) => {
    try {
      const response = await axios.get(`${API}/admin/clients/${client.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClient(response.data.client);
      setClientBookings(response.data.bookings);
      setClientNotes(response.data.client.admin_notes || "");
    } catch (error) {
      toast.error("Failed to load client details");
    }
  };

  const saveClientNotes = async () => {
    if (!selectedClient) return;
    try {
      await axios.put(`${API}/admin/clients/${selectedClient.id}/notes?notes=${encodeURIComponent(clientNotes)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client notes saved!");
      fetchClients();
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const handleGiveReward = async () => {
    if (!selectedClient) return;
    try {
      const response = await axios.post(`${API}/admin/clients/${selectedClient.id}/give-reward`, {
        reward_type: rewardData.type,
        value: rewardData.value,
        reason: rewardData.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      if (response.data.discount_code) {
        toast.success(`Discount Code: ${response.data.discount_code}`, { duration: 10000 });
      }
      setShowGiveReward(false);
      setRewardData({ type: 'loyalty_points', value: 1, reason: '' });
      // Refresh client data
      const clientRes = await axios.get(`${API}/admin/clients/${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClient(clientRes.data.client);
      setClientNotes(clientRes.data.client.admin_notes || "");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to give reward");
    }
  };

  const handleServicePhotoUpload = async (e) => {
    if (!selectedBooking) return;
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhotos(true);
    const formData = new FormData();
    formData.append('photo_type', photoUploadType);
    files.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/upload-service-photos`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' 
        }
      });
      toast.success(`${photoUploadType === 'before' ? 'Before' : 'After'} photos uploaded!`);
      // Refresh booking to show new photos
      const updatedBooking = await axios.get(`${API}/admin/bookings/${selectedBooking.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBooking(updatedBooking.data);
    } catch (error) {
      toast.error("Failed to upload photos");
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  };

  const deletePhoto = async (photoUrl) => {
    if (!selectedBooking) return;
    try {
      await axios.delete(`${API}/admin/bookings/${selectedBooking.id}/photo?photo_url=${encodeURIComponent(photoUrl)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Photo deleted");
      // Refresh booking
      const updatedBooking = await axios.get(`${API}/admin/bookings/${selectedBooking.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBooking(updatedBooking.data);
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client deleted successfully");
      setSelectedClient(null);
      fetchClients(); // Refresh client list
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.email?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.phone?.includes(clientSearch)
  );

  // Update client details
  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    try {
      await axios.put(`${API}/admin/clients/${selectedClient.id}/update`, editClientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Client updated!");
      setEditingClient(false);
      // Refresh client data
      const response = await axios.get(`${API}/admin/clients/${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClient(response.data.client);
      fetchClients();
    } catch (error) {
      toast.error("Failed to update client");
    }
  };

  // Update booking services (package, addons)
  const handleUpdateBookingServices = async () => {
    if (!selectedBooking) return;
    try {
      // Calculate final price with discount
      const subtotal = (editBookingData.package_price || 0) + (editBookingData.addons_price || 0) + (editBookingData.travel_fee || 0);
      const discount = manualDiscount.type === 'percentage' 
        ? subtotal * (manualDiscount.value || 0) / 100 
        : (manualDiscount.value || 0);
      const finalTotal = Math.max(0, subtotal - discount);

      const updateData = {
        ...editBookingData,
        total_price: finalTotal,
        manual_discount: manualDiscount.value > 0 ? manualDiscount : null
      };

      const response = await axios.put(`${API}/admin/bookings/${selectedBooking.id}/full-update`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking updated!");
      setEditingBookingServices(false);
      setManualDiscount({ type: 'percentage', value: 0, reason: '' });
      setSelectedBooking(response.data.booking);
      fetchData();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  // Record payment
  const handleRecordPayment = async (markCompleted = true) => {
    if (!selectedBooking) return;
    const validPayments = payments.filter(p => p.amount > 0);
    if (validPayments.length === 0) {
      toast.error("Please enter at least one payment");
      return;
    }
    try {
      // Transform frontend format to backend format
      const formattedPayments = validPayments.map(p => ({
        payment_method: p.method,
        amount: p.amount,
        notes: p.notes || ""
      }));
      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/record-payment`, {
        payments: formattedPayments,
        mark_completed: markCompleted
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment recorded!");
      setShowPaymentModal(false);
      setPayments([{ method: 'cash', amount: 0, notes: '' }]);
      // Refresh booking
      const updatedBooking = await axios.get(`${API}/admin/bookings/${selectedBooking.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBooking(updatedBooking.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  const addPaymentRow = () => {
    setPayments([...payments, { method: 'cash', amount: 0, notes: '' }]);
  };

  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments(payments.filter((_, i) => i !== index));
    }
  };

  const updatePayment = (index, field, value) => {
    const newPayments = [...payments];
    newPayments[index][field] = value;
    setPayments(newPayments);
  };

  const openBookingForEdit = (booking) => {
    setEditBookingData({
      package: booking.package,
      vehicle_type: booking.vehicle_type,
      addons: booking.addons || [],
      package_price: booking.package_price,
      addons_price: booking.addons_price,
      travel_fee: booking.travel_fee || 0
    });
  };

  const openClientForEdit = () => {
    if (!selectedClient) return;
    setEditClientData({
      name: selectedClient.name,
      phone: selectedClient.phone || '',
      home_address: selectedClient.home_address || '',
      loyalty_points: selectedClient.loyalty_points || 0,
      total_services: selectedClient.total_services || 0,
      admin_notes: selectedClient.admin_notes || ''
    });
    setEditingClient(true);
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Booking ${newStatus.replace(/_/g, ' ')}`);
      fetchData();
      setSelectedBooking(null);
      setShowCancelConfirm(false);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking deleted permanently");
      fetchData();
      setSelectedBooking(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const handleAdminReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error("Please select a new date and time");
      return;
    }
    try {
      await axios.put(`${API}/admin/bookings/${selectedBooking.id}/reschedule`, {
        preferred_date: format(rescheduleDate, "yyyy-MM-dd"),
        preferred_time: rescheduleTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking rescheduled!");
      setShowRescheduleModal(false);
      setSelectedBooking(null);
      setRescheduleDate(null);
      setRescheduleTime("");
      fetchData();
    } catch (error) {
      toast.error("Failed to reschedule booking");
    }
  };

  const [showEditPrice, setShowEditPrice] = useState(false);
  const [editedPrice, setEditedPrice] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const handleUpdateBooking = async (bookingId, updates) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/update`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking updated!");
      fetchData();
      // Refresh selected booking
      const updatedBooking = await axios.get(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBooking(updatedBooking.data);
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const openBookingDetail = async (booking) => {
    setSelectedBooking(booking);
    setEditedPrice(booking.total_price?.toString() || "");
    setAdminNotes(booking.admin_notes || "");
    setShowEditPrice(false);
  };

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="glass rounded-2xl p-8 text-center max-w-md">
          <Shield className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Admin Access Required</h1>
          <p className="text-slate-400 mb-6">Please log in with your admin email to access the dashboard.</p>
          <Button onClick={() => navigate("/account")} className="btn-primary-glow text-slate-900">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Not admin
  if (!loading && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="glass rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-slate-400 mb-6">Your email ({user.email}) is not authorized for admin access.</p>
          <Button onClick={() => navigate("/")} className="bg-slate-700 hover:bg-slate-600">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Calendar days
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay();
  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-12" data-testid="admin-page">
      {/* Admin Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <a href="/" className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="RainXpress Logo" 
                className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover"
              />
            </a>
            <span className="hidden md:inline text-slate-500">|</span>
            <span className="text-cyan-400 font-medium text-sm md:text-base">Admin</span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="hidden md:inline text-slate-400 text-sm">{user.email}</span>
            <Button onClick={() => navigate("/")} variant="outline" className="text-xs md:text-sm px-2 md:px-4">
              Back
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 md:pt-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4 mb-6 md:mb-8">
            <div className="glass rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-3xl font-bold text-cyan-400">{stats.total_bookings}</p>
              <p className="text-slate-400 text-xs md:text-sm">Total</p>
            </div>
            <div className="glass rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-3xl font-bold text-yellow-400">{stats.pending}</p>
              <p className="text-slate-400 text-xs md:text-sm">Pending</p>
            </div>
            <div className="glass rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-3xl font-bold text-blue-400">{stats.confirmed}</p>
              <p className="text-slate-400 text-xs md:text-sm">Confirmed</p>
            </div>
            <div className="glass rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-3xl font-bold text-green-400">{stats.completed}</p>
              <p className="text-slate-400 text-xs md:text-sm">Completed</p>
            </div>
            <div className="glass rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-3xl font-bold text-slate-300">{stats.today_bookings}</p>
              <p className="text-slate-400 text-xs md:text-sm">Today</p>
            </div>
            <div className="glass rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-3xl font-bold text-purple-400">{stats.total_users}</p>
              <p className="text-slate-400 text-xs md:text-sm">Users</p>
            </div>
          </div>
        )}

        {/* View Toggle & Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setView("list")}
              className={`text-xs md:text-sm px-2 md:px-4 ${view === "list" ? "bg-cyan-600" : "bg-slate-700"}`}
              data-testid="view-list-btn"
            >
              List
            </Button>
            <Button
              onClick={() => setView("calendar")}
              className={`text-xs md:text-sm px-2 md:px-4 ${view === "calendar" ? "bg-cyan-600" : "bg-slate-700"}`}
              data-testid="view-calendar-btn"
            >
              Calendar
            </Button>
            <Button
              onClick={() => setView("clients")}
              className={`text-xs md:text-sm px-2 md:px-4 ${view === "clients" ? "bg-cyan-600" : "bg-slate-700"}`}
              data-testid="view-clients-btn"
            >
              <Users className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Clients</span>
            </Button>
            <Button
              onClick={() => setView("sales")}
              className={`text-xs md:text-sm px-2 md:px-4 ${view === "sales" ? "bg-cyan-600" : "bg-slate-700"}`}
              data-testid="view-sales-btn"
            >
              <DollarSign className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Sales</span>
            </Button>
            <Button
              onClick={() => setView("schedule")}
              className={`text-xs md:text-sm px-2 md:px-4 ${view === "schedule" ? "bg-cyan-600" : "bg-slate-700"}`}
              data-testid="view-schedule-btn"
            >
              <Ban className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Block</span>
            </Button>
            <Button
              onClick={() => setView("subscribers")}
              className={`text-xs md:text-sm px-2 md:px-4 ${view === "subscribers" ? "bg-cyan-600" : "bg-slate-700"}`}
              data-testid="view-subscribers-btn"
            >
              <Mail className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Email</span>
            </Button>
          </div>

          {/* Add Booking Button */}
          <Button
            onClick={() => setShowAddBookingModal(true)}
            className="bg-green-600 hover:bg-green-500 text-xs md:text-sm"
          >
            <Plus className="w-4 h-4 mr-1 md:mr-2" />
            Add Booking
          </Button>
        </div>
        
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {view === "list" && (
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_deposit">Pending Deposit</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          )}

          {view === "calendar" && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  if (currentMonth === 1) {
                    setCurrentMonth(12);
                    setCurrentYear(currentYear - 1);
                  } else {
                    setCurrentMonth(currentMonth - 1);
                  }
                }}
                className="bg-slate-700 p-2"
              >
                <ChevronDown className="w-4 h-4 rotate-90" />
              </Button>
              <span className="text-lg font-medium w-40 text-center">
                {new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
              <Button
                onClick={() => {
                  if (currentMonth === 12) {
                    setCurrentMonth(1);
                    setCurrentYear(currentYear + 1);
                  } else {
                    setCurrentMonth(currentMonth + 1);
                  }
                }}
                className="bg-slate-700 p-2"
              >
                <ChevronDown className="w-4 h-4 -rotate-90" />
              </Button>
            </div>
          )}
        </div>

        {/* List View */}
        {view === "list" && (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Booked On</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Service Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Package</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Total</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{booking.customer_name}</p>
                            <p className="text-xs text-slate-500">{booking.make_model}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p>{booking.phone}</p>
                            <p className="text-slate-500 text-xs">{booking.email}</p>
                            <p className="text-cyan-400 text-xs">Prefers: {booking.preferred_contact || 'text'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p className="text-amber-400">{booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '-'}</p>
                            <p className="text-slate-500 text-xs">{booking.created_at ? new Date(booking.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p>{booking.preferred_date}</p>
                            <p className="text-slate-500">{booking.preferred_time}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{booking.package?.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-500">{booking.vehicle_type}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">${booking.total_price?.toFixed(2)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(booking.status)}`}>
                            {formatStatus(booking.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            onClick={() => openBookingDetail(booking)}
                            className="text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {bookings.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No bookings found</p>
                )}
              </div>
            </div>
            
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {bookings.length === 0 ? (
                <div className="glass rounded-xl p-6 text-center">
                  <p className="text-slate-500">No bookings found</p>
                </div>
              ) : (
                bookings.map((booking) => (
                  <div key={booking.id} className="glass rounded-xl p-4" onClick={() => openBookingDetail(booking)}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-white">{booking.customer_name}</p>
                        <p className="text-xs text-slate-400">{booking.make_model}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(booking.status)}`}>
                        {formatStatus(booking.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <p className="text-slate-500 text-xs">Booked On</p>
                        <p className="text-amber-400">{booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '-'}</p>
                        <p className="text-slate-500 text-xs">{booking.created_at ? new Date(booking.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Service Date</p>
                        <p className="text-slate-300">{booking.preferred_date}</p>
                        <p className="text-slate-400 text-xs">{booking.preferred_time}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <p className="text-slate-500 text-xs">Package</p>
                        <p className="text-slate-300 capitalize">{booking.package?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-700 pt-3">
                      <div>
                        <p className="text-slate-500 text-xs">Total</p>
                        <p className="font-bold text-cyan-400">${booking.total_price?.toFixed(2)}</p>
                      </div>
                      <Button className="text-xs bg-slate-700 hover:bg-slate-600 px-4 py-2">
                        View Details
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Calendar View */}
        {view === "calendar" && (
          <div className="glass rounded-xl p-3 md:p-6">
            <div className="grid grid-cols-7 gap-1 md:gap-2 mb-4">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="text-center text-slate-400 text-xs md:text-sm font-medium py-2">
                  <span className="md:hidden">{day}</span>
                  <span className="hidden md:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {calendarDays.map((day, idx) => {
                const dateStr = day ? `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
                const dayBookings = dateStr ? calendarData[dateStr] || [] : [];
                
                // Fix timezone issue - use local date for comparison
                const today = new Date();
                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                const isToday = dateStr === todayStr;
                
                // Check if day is blocked
                const dayBlockedSlots = dateStr ? blockedSlots.filter(s => s.date === dateStr) : [];
                const isFullyBlocked = dayBlockedSlots.some(s => s.time === null);
                const blockedTimes = dayBlockedSlots.filter(s => s.time !== null).map(s => s.time);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-16 md:min-h-24 rounded-lg p-1 md:p-2 ${
                      day ? (isFullyBlocked ? 'bg-red-900/30 border border-red-500/30' : 'bg-slate-800/50 hover:bg-slate-800') : ''
                    } ${isToday ? 'ring-2 ring-cyan-400' : ''}`}
                  >
                    {day && (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-xs md:text-sm font-medium ${isToday ? 'text-cyan-400' : isFullyBlocked ? 'text-red-400' : 'text-slate-300'}`}>
                            {day}
                          </p>
                          {isFullyBlocked && (
                            <Ban className="w-3 h-3 text-red-400 hidden md:block" />
                          )}
                        </div>
                        {isFullyBlocked ? (
                          <div className="text-[10px] md:text-xs text-red-400 bg-red-500/10 rounded px-1 py-0.5">
                            <span className="md:hidden">X</span>
                            <span className="hidden md:inline">Blocked</span>
                            {dayBlockedSlots[0]?.reason && (
                              <span className="hidden md:block text-red-300 truncate">{dayBlockedSlots[0].reason}</span>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {blockedTimes.length > 0 && (() => {
                              // Convert blocked times to ranges
                              const uniqueTimes = [...new Set(blockedTimes)];
                              const sortedTimes = uniqueTimes.sort((a, b) => TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b));
                              const ranges = [];
                              let rangeStart = sortedTimes[0];
                              let rangeEnd = sortedTimes[0];
                              
                              for (let i = 1; i < sortedTimes.length; i++) {
                                const currentIndex = TIME_SLOTS.indexOf(sortedTimes[i]);
                                const prevIndex = TIME_SLOTS.indexOf(rangeEnd);
                                if (currentIndex === prevIndex + 1) {
                                  rangeEnd = sortedTimes[i];
                                } else {
                                  ranges.push({ start: rangeStart, end: rangeEnd });
                                  rangeStart = sortedTimes[i];
                                  rangeEnd = sortedTimes[i];
                                }
                              }
                              ranges.push({ start: rangeStart, end: rangeEnd });
                              
                              return (
                                <div className="text-xs text-red-400 bg-red-500/10 rounded px-1 py-0.5">
                                  <div className="flex items-center gap-1">
                                    <Ban className="w-2 h-2 flex-shrink-0" />
                                    <span className="truncate">
                                      {ranges.slice(0, 2).map((r, i) => (
                                        <span key={i}>
                                          {r.start === r.end ? r.start.replace(' ', '') : `${r.start.replace(' ', '')}-${r.end.replace(' ', '')}`}
                                          {i < Math.min(ranges.length, 2) - 1 ? ', ' : ''}
                                        </span>
                                      ))}
                                      {ranges.length > 2 && '...'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                            {dayBookings.slice(0, blockedTimes.length > 0 ? 2 : 3).map((b, i) => (
                              <div
                                key={i}
                                onClick={() => openBookingDetail(b)}
                                className={`text-xs p-1.5 rounded cursor-pointer ${getStatusColor(b.status)} hover:opacity-80`}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <span className="font-medium truncate">{b.preferred_time}</span>
                                  <span className={`px-1 py-0.5 rounded text-[10px] ${b.user_id ? 'bg-green-500/30 text-green-300' : 'bg-amber-500/30 text-amber-300'}`}>
                                    {b.user_id ? 'Acc' : 'Guest'}
                                  </span>
                                </div>
                                <div className="truncate text-slate-300">{b.customer_name?.split(' ')[0]}</div>
                                <div className="truncate text-slate-400 text-[10px]">
                                  {b.phone || b.email?.split('@')[0] || ''}
                                </div>
                              </div>
                            ))}
                            {dayBookings.length > (blockedTimes.length > 0 ? 2 : 3) && (
                              <p className="text-xs text-slate-500">+{dayBookings.length - (blockedTimes.length > 0 ? 2 : 3)} more</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-slate-700 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-900/50 border border-red-500/30"></div>
                <span className="text-slate-400">Blocked</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-green-500/20"></div>
                <span className="text-slate-400">Completed</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-yellow-500/20"></div>
                <span className="text-slate-400">Pending</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-cyan-500/20"></div>
                <span className="text-slate-400">Confirmed</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded bg-green-500/30 text-green-300 text-[10px]">Acc</span>
                <span className="text-slate-400">= Account</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-300 text-[10px]">Guest</span>
                <span className="text-slate-400">= Guest</span>
              </div>
            </div>
          </div>
        )}

        {/* Clients View */}
        {view === "clients" && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search clients by name, email, or phone..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700"
                  data-testid="client-search-input"
                />
              </div>
            </div>

            {/* Client List Table */}
            <div className="glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Client</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Bookings</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Total Spent</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Loyalty</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Last Booking</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {filteredClients.map((client) => (
                      <tr key={client.id} className="hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{client.name}</p>
                            <p className="text-xs text-slate-500">{client.home_address?.substring(0, 30) || 'No address'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm space-y-1">
                            <p className="truncate max-w-[140px]">{client.phone || 'N/A'}</p>
                            <p className="text-slate-500 text-xs truncate max-w-[140px]">{client.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-cyan-400 font-medium">{client.booking_count}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-green-400 font-medium">${client.total_spent?.toFixed(2) || '0.00'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p className="text-purple-400">{client.loyalty_points || 0} pts</p>
                            <p className="text-xs text-slate-500">{client.total_services || 0} services</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {client.last_booking || 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            onClick={() => openClientDetail(client)}
                            className="text-xs bg-slate-700 hover:bg-slate-600 px-3 py-1"
                            data-testid={`view-client-${client.id}`}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredClients.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No clients found</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sales Report View */}
        {view === "sales" && (
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="glass rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex gap-2">
                  {["day", "week", "month", "custom"].map((period) => (
                    <Button
                      key={period}
                      onClick={() => setSalesPeriod(period)}
                      className={salesPeriod === period ? "bg-cyan-600" : "bg-slate-700"}
                      data-testid={`sales-period-${period}`}
                    >
                      {period === "day" ? "Today" : period === "week" ? "This Week" : period === "month" ? "This Month" : "Custom Range"}
                    </Button>
                  ))}
                </div>
                
                {salesPeriod === "custom" && (
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button className="bg-slate-700 text-sm">
                          <Calendar className="w-4 h-4 mr-2" />
                          {customStartDate ? format(customStartDate, "MMM d, yyyy") : "Start Date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                        <CalendarComponent
                          mode="single"
                          selected={customStartDate}
                          onSelect={setCustomStartDate}
                          className="rounded-md"
                        />
                      </PopoverContent>
                    </Popover>
                    <span className="text-slate-500">to</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button className="bg-slate-700 text-sm">
                          <Calendar className="w-4 h-4 mr-2" />
                          {customEndDate ? format(customEndDate, "MMM d, yyyy") : "End Date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                        <CalendarComponent
                          mode="single"
                          selected={customEndDate}
                          onSelect={setCustomEndDate}
                          className="rounded-md"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                
                <Button onClick={fetchSalesReport} className="bg-slate-600 hover:bg-slate-500" disabled={loadingSales}>
                  {loadingSales ? "Loading..." : "Refresh"}
                </Button>
              </div>
              
              {salesReport && (
                <p className="text-slate-400 text-sm mt-3">
                  Showing data from {salesReport.start_date} to {salesReport.end_date}
                </p>
              )}
            </div>

            {loadingSales ? (
              <div className="flex justify-center py-12">
                <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : salesReport ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">${salesReport.summary.total_revenue.toLocaleString()}</p>
                    <p className="text-slate-400 text-sm">Total Revenue</p>
                  </div>
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-cyan-400">{salesReport.summary.total_bookings}</p>
                    <p className="text-slate-400 text-sm">Total Bookings</p>
                  </div>
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-purple-400">{salesReport.summary.completed_bookings}</p>
                    <p className="text-slate-400 text-sm">Completed</p>
                  </div>
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-yellow-400">${salesReport.summary.avg_booking_value.toFixed(0)}</p>
                    <p className="text-slate-400 text-sm">Avg. Booking Value</p>
                  </div>
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">{salesReport.summary.completion_rate}%</p>
                    <p className="text-slate-400 text-sm">Completion Rate</p>
                  </div>
                </div>

                {/* Detailed Breakdowns */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Payment Methods */}
                  <div className="glass rounded-xl p-5">
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      Payment Methods
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(salesReport.payment_methods).map(([method, amount]) => (
                        <div key={method} className="flex justify-between items-center">
                          <span className="text-slate-400 capitalize">{method.replace('_', ' ')}</span>
                          <span className="text-white font-medium">${amount.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="border-t border-slate-700 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300 font-medium">Deposits Collected</span>
                          <span className="text-cyan-400 font-bold">${salesReport.summary.total_deposits.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Package Breakdown */}
                  <div className="glass rounded-xl p-5">
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      Packages
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(salesReport.package_breakdown).map(([pkg, data]) => (
                        <div key={pkg} className="flex justify-between items-center">
                          <div>
                            <span className="text-white capitalize">{pkg.replace(/_/g, ' ')}</span>
                            <span className="text-slate-500 text-xs ml-2">({data.count})</span>
                          </div>
                          <span className="text-green-400 font-medium">${data.revenue.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Vehicle Breakdown */}
                  <div className="glass rounded-xl p-5">
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <Car className="w-5 h-5 text-cyan-400" />
                      Vehicle Types
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(salesReport.vehicle_breakdown).map(([vehicle, data]) => (
                        <div key={vehicle} className="flex justify-between items-center">
                          <div>
                            <span className="text-white capitalize">{vehicle}</span>
                            <span className="text-slate-500 text-xs ml-2">({data.count})</span>
                          </div>
                          <span className="text-green-400 font-medium">${data.revenue.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Daily Chart */}
                <div className="glass rounded-xl p-5">
                  <h3 className="text-lg font-medium mb-4">Daily Revenue</h3>
                  <div className="overflow-x-auto">
                    <div className="flex gap-2 min-w-max pb-2">
                      {Object.entries(salesReport.daily_data).map(([date, data]) => {
                        const maxRevenue = Math.max(...Object.values(salesReport.daily_data).map(d => d.revenue));
                        const height = maxRevenue > 0 ? (data.revenue / maxRevenue * 100) : 0;
                        return (
                          <div key={date} className="flex flex-col items-center" style={{ width: '60px' }}>
                            <div className="w-10 bg-slate-800 rounded-t relative" style={{ height: '120px' }}>
                              <div
                                className="absolute bottom-0 w-full bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t transition-all"
                                style={{ height: `${Math.max(height, 2)}%` }}
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-2">{date.slice(5)}</p>
                            <p className="text-xs text-cyan-400">${data.revenue}</p>
                            <p className="text-xs text-slate-600">{data.bookings} bookings</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Booking Status Breakdown */}
                <div className="glass rounded-xl p-5">
                  <h3 className="text-lg font-medium mb-4">Booking Status</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-400">{salesReport.summary.completed_bookings}</p>
                      <p className="text-sm text-green-400/70">Completed</p>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-400">{salesReport.summary.pending_bookings}</p>
                      <p className="text-sm text-yellow-400/70">Pending/In Progress</p>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-400">{salesReport.summary.cancelled_bookings}</p>
                      <p className="text-sm text-red-400/70">Cancelled</p>
                    </div>
                    <div className="bg-slate-500/10 border border-slate-500/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-slate-300">{salesReport.summary.cancellation_rate}%</p>
                      <p className="text-sm text-slate-400">Cancellation Rate</p>
                    </div>
                  </div>
                </div>

                {/* Add-ons Breakdown */}
                {Object.keys(salesReport.addon_breakdown).length > 0 && (
                  <div className="glass rounded-xl p-5">
                    <h3 className="text-lg font-medium mb-4">Popular Add-ons</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {Object.entries(salesReport.addon_breakdown)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([addon, data]) => (
                          <div key={addon} className="bg-slate-800/50 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-purple-400">{data.count}</p>
                            <p className="text-xs text-slate-400 capitalize">{addon.replace(/_/g, ' ')}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="glass rounded-xl p-8 text-center">
                <p className="text-slate-400">Select a period to view sales report</p>
              </div>
            )}
          </div>
        )}

        {/* Schedule / Block Dates View */}
        {view === "schedule" && (
          <div className="space-y-4 md:space-y-6">
            <div className="glass rounded-xl p-4 md:p-6">
              <h3 className="text-base md:text-lg font-medium mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-400" />
                Block Booking Slots
              </h3>
              
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
                <Button 
                  onClick={() => {
                    const today = new Date();
                    setBlockDate(today);
                    setBlockWholeDay(true);
                    setBlockReason("Fully booked");
                  }}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20 text-xs md:text-sm"
                >
                  <Ban className="w-4 h-4 mr-1 md:mr-2" />
                  Block Today
                </Button>
                <Button 
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setBlockDate(tomorrow);
                    setBlockWholeDay(true);
                    setBlockReason("Fully booked");
                  }}
                  variant="outline"
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20 text-xs md:text-sm"
                >
                  <Ban className="w-4 h-4 mr-1 md:mr-2" />
                  Block Tomorrow
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div className="flex justify-center md:justify-start">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Select Date to Block</Label>
                    <CalendarComponent
                      mode="single"
                      selected={blockDate}
                      onSelect={setBlockDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      className="rounded-lg border border-slate-700 bg-slate-800"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={blockWholeDay}
                      onChange={(e) => setBlockWholeDay(e.target.checked)}
                      className="rounded"
                    />
                    <Label>Block entire day</Label>
                  </div>
                  
                  {!blockWholeDay && (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Start Time</Label>
                        <Select value={blockStartTime} onValueChange={setBlockStartTime}>
                          <SelectTrigger className="bg-slate-800 border-slate-700">
                            <SelectValue placeholder="Select start time" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                            {TIME_SLOTS.map((time) => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-sm font-medium mb-2 block">End Time (optional - for range)</Label>
                        <Select value={blockEndTime} onValueChange={setBlockEndTime}>
                          <SelectTrigger className="bg-slate-800 border-slate-700">
                            <SelectValue placeholder="Same as start (single slot)" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                            {TIME_SLOTS.filter(time => {
                              if (!blockStartTime) return true;
                              return TIME_SLOTS.indexOf(time) >= TIME_SLOTS.indexOf(blockStartTime);
                            }).map((time) => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {blockStartTime && blockEndTime && blockStartTime !== blockEndTime && (
                        <p className="text-cyan-400 text-sm">
                          Will block {TIME_SLOTS.slice(TIME_SLOTS.indexOf(blockStartTime), TIME_SLOTS.indexOf(blockEndTime) + 1).length} time slots
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Reason (shown to customers)</Label>
                    <Input
                      placeholder="e.g., Holiday, Personal day off, Fully booked"
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  
                  <Button onClick={handleBlockSlot} className="bg-red-600 hover:bg-red-500 w-full">
                    <Ban className="w-4 h-4 mr-2" />
                    {blockWholeDay ? "Block Entire Day" : 
                     blockStartTime && blockEndTime && blockStartTime !== blockEndTime ? 
                     `Block ${blockStartTime} - ${blockEndTime}` : 
                     blockStartTime ? `Block ${blockStartTime}` : "Select times to block"}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Currently Blocked Slots */}
            <div className="glass rounded-xl p-4 md:p-6">
              <h3 className="text-base md:text-lg font-medium mb-4">Currently Blocked</h3>
              {blockedSlots.length === 0 ? (
                <p className="text-slate-400 text-center py-4">No blocked slots</p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    // Group blocked slots by date and find consecutive time ranges
                    const groupedByDate = {};
                    blockedSlots.forEach(slot => {
                      if (!groupedByDate[slot.date]) {
                        groupedByDate[slot.date] = { wholeDayBlocked: false, times: [], reason: slot.reason };
                      }
                      if (slot.time === null) {
                        groupedByDate[slot.date].wholeDayBlocked = true;
                        groupedByDate[slot.date].reason = slot.reason;
                      } else {
                        groupedByDate[slot.date].times.push(slot.time);
                      }
                    });
                    
                    // Convert times to ranges
                    const getTimeRanges = (times) => {
                      if (times.length === 0) return [];
                      // Remove duplicates and sort by TIME_SLOTS order
                      const uniqueTimes = [...new Set(times)];
                      const sortedTimes = uniqueTimes.sort((a, b) => TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b));
                      const ranges = [];
                      let rangeStart = sortedTimes[0];
                      let rangeEnd = sortedTimes[0];
                      
                      for (let i = 1; i < sortedTimes.length; i++) {
                        const currentIndex = TIME_SLOTS.indexOf(sortedTimes[i]);
                        const prevIndex = TIME_SLOTS.indexOf(rangeEnd);
                        
                        if (currentIndex === prevIndex + 1) {
                          // Consecutive, extend range
                          rangeEnd = sortedTimes[i];
                        } else {
                          // Gap found, save current range and start new one
                          ranges.push({ start: rangeStart, end: rangeEnd });
                          rangeStart = sortedTimes[i];
                          rangeEnd = sortedTimes[i];
                        }
                      }
                      // Save the last range
                      ranges.push({ start: rangeStart, end: rangeEnd });
                      return ranges;
                    };
                    
                    return Object.entries(groupedByDate).map(([date, data]) => {
                      const timeRanges = getTimeRanges(data.times);
                      return (
                        <div key={date} className="bg-slate-800/50 rounded-lg px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Ban className="w-4 h-4 text-red-400" />
                              <div>
                                <p className="font-medium">{date}</p>
                                {data.wholeDayBlocked ? (
                                  <p className="text-red-400 text-sm">Whole Day Blocked</p>
                                ) : (
                                  <p className="text-sm text-slate-400">
                                    {timeRanges.map((range, idx) => (
                                      <span key={idx}>
                                        {range.start === range.end 
                                          ? range.start 
                                          : `${range.start} to ${range.end}`}
                                        {idx < timeRanges.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </p>
                                )}
                                {data.reason && <p className="text-slate-500 text-xs mt-1">{data.reason}</p>}
                              </div>
                            </div>
                            <Button
                              onClick={async () => {
                                // Unblock all slots for this date
                                if (data.wholeDayBlocked) {
                                  await handleUnblockSlot(date, null);
                                }
                                for (const time of data.times) {
                                  await handleUnblockSlot(date, time);
                                }
                              }}
                              className="bg-slate-700 hover:bg-slate-600 text-xs"
                            >
                              Unblock All
                            </Button>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscribers / Email List View */}
        {view === "subscribers" && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-cyan-400">{subscriberStats.total}</p>
                <p className="text-slate-400 text-sm">Total Subscribers</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-400">{subscriberStats.with_profile}</p>
                <p className="text-slate-400 text-sm">With Account</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">{subscriberStats.guests}</p>
                <p className="text-slate-400 text-sm">Guests Only</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Email Subscribers</h3>
              <Button
                onClick={() => setShowEmailComposer(true)}
                className="bg-cyan-600 hover:bg-cyan-500"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email Campaign
              </Button>
            </div>

            {/* Subscribers Table */}
            <div className="glass rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Discount Code</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Source</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Date</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub, idx) => (
                    <tr key={idx} className="border-t border-slate-700/50 hover:bg-slate-800/30">
                      <td className="p-4 text-sm">{sub.email}</td>
                      <td className="p-4">
                        {sub.has_profile ? (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                            Account
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">
                            Guest
                          </span>
                        )}
                        {sub.unsubscribed && (
                          <span className="ml-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">
                            Unsub
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-sm font-mono text-cyan-400">{sub.discount_code || '-'}</td>
                      <td className="p-4">
                        {sub.code_used ? (
                          <span className="text-slate-500 text-xs">Used</span>
                        ) : (
                          <span className="text-green-400 text-xs">Active</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-slate-400">{sub.source || '-'}</td>
                      <td className="p-4 text-sm text-slate-400">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <button
                          onClick={async () => {
                            if (window.confirm(`Delete subscriber ${sub.email}?`)) {
                              try {
                                await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(sub.email)}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success("Subscriber deleted");
                                fetchSubscribers();
                              } catch (error) {
                                toast.error("Failed to delete subscriber");
                              }
                            }
                          }}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Delete subscriber"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscribers.length === 0 && (
                <p className="text-center text-slate-400 py-8">No subscribers yet</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Email Composer Modal */}
      <AnimatePresence>
        {showEmailComposer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setShowEmailComposer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-2xl w-full my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Send Email Campaign</h2>
                <button onClick={() => setShowEmailComposer(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Recipients *</Label>
                  <Select value={emailData.recipient_type} onValueChange={(v) => setEmailData({...emailData, recipient_type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all">All ({subscriberStats.total} recipients)</SelectItem>
                      <SelectItem value="with_profile">Accounts Only ({subscriberStats.with_profile} recipients)</SelectItem>
                      <SelectItem value="guests_only">Guests Only ({subscriberStats.guests} recipients)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Subject Line *</Label>
                  <Input
                    placeholder="e.g., Special Offer Inside!"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Email Content (HTML supported) *</Label>
                  <textarea
                    placeholder="<h2>Hello!</h2><p>We have a special offer for you...</p>"
                    value={emailData.content}
                    onChange={(e) => setEmailData({...emailData, content: e.target.value})}
                    className="w-full h-48 bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Tip: Use &lt;h2&gt;, &lt;p&gt;, &lt;a href=""&gt; tags. Content will be wrapped in RainXpress template.
                  </p>
                </div>

                <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <Checkbox
                    id="test-mode"
                    checked={emailData.test_mode}
                    onCheckedChange={(checked) => setEmailData({...emailData, test_mode: checked})}
                  />
                  <label htmlFor="test-mode" className="text-sm text-amber-400 cursor-pointer">
                    Test Mode - Send only to admin email first
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowEmailComposer(false)}
                    className="flex-1 bg-slate-700 hover:bg-slate-600"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendBulkEmail}
                    disabled={sendingEmail || !emailData.subject || !emailData.content}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        {emailData.test_mode ? "Send Test" : "Send Campaign"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Booking Modal */}
      <AnimatePresence>
        {showAddBookingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setShowAddBookingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-5 max-w-2xl w-full my-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 sticky top-0 bg-slate-900/95 backdrop-blur-sm -mx-5 -mt-5 px-5 pt-5 pb-3 border-b border-slate-700 z-10">
                <h2 className="text-lg font-bold">Add New Booking</h2>
                <button onClick={() => setShowAddBookingModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Client Search */}
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Search Existing Client (optional)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Start typing name, email, or phone..."
                    value={clientSearchForBooking}
                    onChange={(e) => setClientSearchForBooking(e.target.value)}
                    className="bg-slate-800 border-slate-700 pl-10"
                  />
                  {filteredClientsForBooking.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden z-50 max-h-64 overflow-y-auto shadow-xl">
                      {filteredClientsForBooking.slice(0, 8).map((client) => (
                        <button
                          key={client.id}
                          onClick={() => {
                            setSelectedClientForBooking(client);
                            setNewBookingData({
                              ...newBookingData,
                              customer_name: client.name,
                              phone: client.phone || "",
                              email: client.email,
                              address: client.home_address || ""
                            });
                            setClientSearchForBooking("");
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-slate-700 flex items-center gap-3 border-b border-slate-700 last:border-0"
                        >
                          <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{client.name}</span>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                              <span>{client.phone || "No phone"}</span>
                              <span>•</span>
                              <span className="truncate">{client.email}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {clientSearchForBooking.length > 0 && filteredClientsForBooking.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg p-4 z-50 text-center">
                      <p className="text-slate-400 text-sm mb-2">No clients found. Fill in details below for a new customer.</p>
                    </div>
                  )}
                </div>
                {selectedClientForBooking && (
                  <div className="mt-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg px-3 py-2 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400 text-sm">Booking for: <strong>{selectedClientForBooking.name}</strong></span>
                    </div>
                    <button 
                      onClick={() => {
                        setSelectedClientForBooking(null);
                        setNewBookingData({
                          ...newBookingData,
                          customer_name: "", phone: "", email: "", address: ""
                        });
                      }} 
                      className="text-slate-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {/* Create Profile Button - shown when no client selected and details filled */}
                {!selectedClientForBooking && newBookingData.customer_name && newBookingData.email && newBookingData.phone && (
                  <div className="mt-2 bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-400 text-sm font-medium">New Customer?</p>
                        <p className="text-slate-400 text-xs">Create an account so they can manage bookings online</p>
                      </div>
                      <Button
                        type="button"
                        onClick={handleCreateClientAccount}
                        disabled={creatingClient}
                        className="bg-purple-600 hover:bg-purple-500 text-sm"
                        size="sm"
                      >
                        {creatingClient ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Users className="w-4 h-4 mr-1" />
                            Create Profile
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-slate-500">Customer Name *</Label>
                  <Input
                    value={newBookingData.customer_name}
                    onChange={(e) => setNewBookingData({...newBookingData, customer_name: e.target.value})}
                    className="bg-slate-800 border-slate-700 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone *</Label>
                  <Input
                    value={newBookingData.phone}
                    onChange={(e) => setNewBookingData({...newBookingData, phone: e.target.value})}
                    className="bg-slate-800 border-slate-700 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input
                    value={newBookingData.email}
                    onChange={(e) => setNewBookingData({...newBookingData, email: e.target.value})}
                    className="bg-slate-800 border-slate-700 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Address</Label>
                  <Input
                    value={newBookingData.address}
                    onChange={(e) => setNewBookingData({...newBookingData, address: e.target.value})}
                    className="bg-slate-800 border-slate-700 h-9"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-slate-500">Vehicle Type</Label>
                  <Select value={newBookingData.vehicle_type} onValueChange={(v) => setNewBookingData({...newBookingData, vehicle_type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large</SelectItem>
                      <SelectItem value="xl">XL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Make & Model</Label>
                  <Input
                    value={newBookingData.make_model}
                    onChange={(e) => setNewBookingData({...newBookingData, make_model: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-slate-500">Package</Label>
                  <Select 
                    value={newBookingData.is_custom_package ? "custom" : newBookingData.package} 
                    onValueChange={(v) => {
                      if (v === "custom") {
                        setNewBookingData({...newBookingData, is_custom_package: true, package: ""});
                      } else {
                        setNewBookingData({...newBookingData, is_custom_package: false, package: v, custom_package_name: "", custom_price: ""});
                      }
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="xpress_clean">Xpress Clean</SelectItem>
                      <SelectItem value="interior_xpress">Interior Xpress Detail</SelectItem>
                      <SelectItem value="deal_xpress">Deal Xpress Detail</SelectItem>
                      <SelectItem value="full_xpress">Full Xpress Detail</SelectItem>
                      <SelectItem value="custom" className="text-cyan-400">+ Custom Package</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Date *</Label>
                  <Input
                    type="date"
                    value={newBookingData.preferred_date}
                    onChange={(e) => setNewBookingData({...newBookingData, preferred_date: e.target.value})}
                    className="bg-slate-800 border-slate-700 h-9"
                  />
                </div>
              </div>
              
              {/* Custom Package Fields */}
              {newBookingData.is_custom_package && (
                <div className="mb-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <Label className="text-xs font-medium text-cyan-400 mb-2 block">Custom Package Details</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Package Name *</Label>
                      <Input
                        placeholder="e.g., Interior Only..."
                        value={newBookingData.custom_package_name}
                        onChange={(e) => setNewBookingData({...newBookingData, custom_package_name: e.target.value})}
                        className="bg-slate-800 border-slate-700 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Total Price ($) *</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 150"
                        value={newBookingData.custom_price}
                        onChange={(e) => setNewBookingData({...newBookingData, custom_price: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs text-slate-500">Time *</Label>
                  <Select value={newBookingData.preferred_time} onValueChange={(v) => setNewBookingData({...newBookingData, preferred_time: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                      {TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Admin Notes</Label>
                  <Input
                    placeholder="Internal notes..."
                    value={newBookingData.admin_notes}
                    onChange={(e) => setNewBookingData({...newBookingData, admin_notes: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              
              {/* Add-ons Selection */}
              <div className="mb-4">
                <Label className="text-xs text-slate-500 mb-2 block">Add-ons (optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ADDONS.map((addon) => (
                    <label
                      key={addon.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                        newBookingData.addons.includes(addon.id)
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newBookingData.addons.includes(addon.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewBookingData({...newBookingData, addons: [...newBookingData.addons, addon.id]});
                            } else {
                              setNewBookingData({...newBookingData, addons: newBookingData.addons.filter(a => a !== addon.id)});
                            }
                          }}
                          className="rounded border-slate-600"
                        />
                        <span className="text-sm">{addon.name}</span>
                      </div>
                      <span className="text-cyan-400 text-sm font-medium">+${addon.price}</span>
                    </label>
                  ))}
                </div>
                
                {/* Custom Add-ons */}
                <div className="mt-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-cyan-400">Custom Add-ons</Label>
                    <Button
                      type="button"
                      onClick={() => {
                        setNewBookingData({
                          ...newBookingData,
                          custom_addons: [...newBookingData.custom_addons, { name: "", price: "" }]
                        });
                      }}
                      className="text-xs bg-cyan-600 hover:bg-cyan-500 px-3 py-1 h-7"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Custom
                    </Button>
                  </div>
                  
                  {newBookingData.custom_addons.length === 0 ? (
                    <p className="text-slate-500 text-xs text-center py-2">No custom add-ons added</p>
                  ) : (
                    <div className="space-y-2">
                      {newBookingData.custom_addons.map((addon, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            placeholder="Add-on name"
                            value={addon.name}
                            onChange={(e) => {
                              const updated = [...newBookingData.custom_addons];
                              updated[index].name = e.target.value;
                              setNewBookingData({...newBookingData, custom_addons: updated});
                            }}
                            className="bg-slate-700 border-slate-600 text-sm flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="$"
                            value={addon.price}
                            onChange={(e) => {
                              const updated = [...newBookingData.custom_addons];
                              updated[index].price = e.target.value;
                              setNewBookingData({...newBookingData, custom_addons: updated});
                            }}
                            className="bg-slate-700 border-slate-600 text-sm w-20"
                          />
                          <Button
                            type="button"
                            onClick={() => {
                              const updated = newBookingData.custom_addons.filter((_, i) => i !== index);
                              setNewBookingData({...newBookingData, custom_addons: updated});
                            }}
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mb-4">
                <Label className="text-xs text-slate-500">Special Instructions</Label>
                <Textarea
                  value={newBookingData.special_instructions}
                  onChange={(e) => setNewBookingData({...newBookingData, special_instructions: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  rows={2}
                />
              </div>
              
              <Button onClick={handleAdminCreateBooking} className="w-full bg-green-600 hover:bg-green-500">
                <Plus className="w-4 h-4 mr-2" />
                Create Booking
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Client Detail Modal */}
      <AnimatePresence>
        {selectedClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => { setSelectedClient(null); setEditingClient(false); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Client Details</h2>
                <div className="flex items-center gap-2">
                  {!editingClient && (
                    <>
                      <Button onClick={openClientForEdit} className="bg-cyan-600 hover:bg-cyan-500 text-xs">
                        <Edit3 className="w-4 h-4 mr-1" /> Edit Client
                      </Button>
                      <Button onClick={() => setShowGiveReward(true)} className="bg-purple-600 hover:bg-purple-500 text-xs">
                        <Gift className="w-4 h-4 mr-1" /> Give Reward
                      </Button>
                      <Button onClick={() => deleteClient(selectedClient.id)} className="bg-red-600 hover:bg-red-500 text-xs">
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </>
                  )}
                  <button onClick={() => { setSelectedClient(null); setEditingClient(false); setShowGiveReward(false); }} className="text-slate-400 hover:text-white">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Give Reward Panel */}
              {showGiveReward && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                  <h3 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Give Reward to {selectedClient.name}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Reward Type</label>
                      <select
                        value={rewardData.type}
                        onChange={(e) => setRewardData({...rewardData, type: e.target.value, value: e.target.value === 'discount_code' ? 10 : 1})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="loyalty_points">Loyalty Points</option>
                        <option value="free_service">Free Service</option>
                        <option value="discount_code">Discount Code (%)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">
                        {rewardData.type === 'loyalty_points' ? 'Points to Add' : 
                         rewardData.type === 'free_service' ? 'Free Services' : 
                         'Discount Percentage'}
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={rewardData.value}
                        onChange={(e) => setRewardData({...rewardData, value: parseInt(e.target.value) || 1})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="text-slate-500 text-xs block mb-1">Reason (optional)</label>
                    <Input
                      placeholder="e.g., Birthday, Referral bonus, Compensation..."
                      value={rewardData.reason}
                      onChange={(e) => setRewardData({...rewardData, reason: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleGiveReward} className="bg-purple-600 hover:bg-purple-500 text-xs">
                      Give Reward
                    </Button>
                    <Button onClick={() => setShowGiveReward(false)} className="bg-slate-600 hover:bg-slate-500 text-xs">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Editable Client Info */}
              {editingClient ? (
                <div className="bg-slate-800/50 rounded-lg p-4 mb-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Name</label>
                      <Input
                        value={editClientData.name || ''}
                        onChange={(e) => setEditClientData({...editClientData, name: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Phone</label>
                      <Input
                        value={editClientData.phone || ''}
                        onChange={(e) => setEditClientData({...editClientData, phone: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-slate-500 text-xs block mb-1">Home Address</label>
                      <Input
                        value={editClientData.home_address || ''}
                        onChange={(e) => setEditClientData({...editClientData, home_address: e.target.value})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Loyalty Points</label>
                      <Input
                        type="number"
                        value={editClientData.loyalty_points || 0}
                        onChange={(e) => setEditClientData({...editClientData, loyalty_points: parseInt(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Total Services</label>
                      <Input
                        type="number"
                        value={editClientData.total_services || 0}
                        onChange={(e) => setEditClientData({...editClientData, total_services: parseInt(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleUpdateClient} className="bg-green-600 hover:bg-green-500 text-xs">Save Changes</Button>
                    <Button onClick={() => setEditingClient(false)} className="bg-slate-600 hover:bg-slate-500 text-xs">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div>
                    <p className="text-slate-500 text-xs">Name</p>
                    <p className="font-medium">{selectedClient.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Email</p>
                    <p>{selectedClient.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Phone</p>
                    <a href={`tel:${selectedClient.phone}`} className="text-cyan-400">{selectedClient.phone || 'N/A'}</a>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500 text-xs">Home Address</p>
                    <p className="text-sm">{selectedClient.home_address || 'Not set'}</p>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <p className="text-slate-500 text-xs">Loyalty Points</p>
                      <p className="text-purple-400 font-bold text-lg">{selectedClient.loyalty_points || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Total Services</p>
                      <p className="text-cyan-400 font-bold text-lg">{selectedClient.total_services || 0}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Client Notes - Larger */}
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                <p className="text-slate-400 text-sm font-medium mb-2">Client Notes</p>
                <Textarea
                  placeholder="Add notes about this client (preferences, vehicle details, special instructions, allergies, etc.)..."
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-sm min-h-[120px]"
                  rows={6}
                  data-testid="client-notes-textarea"
                />
                <Button
                  onClick={saveClientNotes}
                  className="mt-2 bg-cyan-600 hover:bg-cyan-500 text-xs"
                >
                  Save Notes
                </Button>
              </div>

              {/* Client Photos from All Bookings */}
              {clientBookings.some(b => b.customer_photos?.length || b.before_photos?.length || b.after_photos?.length) && (
                <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                  <p className="text-slate-400 text-sm font-medium mb-3">Photos from Services</p>
                  <div className="space-y-4">
                    {clientBookings.filter(b => b.customer_photos?.length || b.before_photos?.length || b.after_photos?.length).map((booking) => (
                      <div key={booking.id} className="border-b border-slate-700 pb-3 last:border-0">
                        <p className="text-xs text-slate-500 mb-2">{booking.preferred_date} - {booking.package?.replace(/_/g, ' ')}</p>
                        <div className="flex flex-wrap gap-2">
                          {booking.customer_photos?.map((photo, idx) => (
                            <img 
                              key={`c-${idx}`}
                              src={`${BACKEND_URL}${photo}`} 
                              alt={`Customer ${idx + 1}`}
                              className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-75 border border-slate-600"
                              onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                            />
                          ))}
                          {booking.before_photos?.map((photo, idx) => (
                            <div key={`b-${idx}`} className="relative">
                              <img 
                                src={`${BACKEND_URL}${photo}`} 
                                alt={`Before ${idx + 1}`}
                                className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-75 border-2 border-yellow-500"
                                onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                              />
                              <span className="absolute -top-1 -left-1 bg-yellow-500 text-black text-xs px-1 rounded">B</span>
                            </div>
                          ))}
                          {booking.after_photos?.map((photo, idx) => (
                            <div key={`a-${idx}`} className="relative">
                              <img 
                                src={`${BACKEND_URL}${photo}`} 
                                alt={`After ${idx + 1}`}
                                className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-75 border-2 border-green-500"
                                onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                              />
                              <span className="absolute -top-1 -left-1 bg-green-500 text-black text-xs px-1 rounded">A</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Booking History */}
              <div>
                <h3 className="text-lg font-medium mb-4">Booking History ({clientBookings.length})</h3>
                {clientBookings.length === 0 ? (
                  <p className="text-slate-500 text-sm">No bookings yet</p>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {clientBookings.map((booking) => (
                      <div 
                        key={booking.id} 
                        className="bg-slate-800 rounded-lg p-3 cursor-pointer hover:bg-slate-700"
                        onClick={() => { 
                          setPreviousClient(selectedClient);
                          setPreviousView('clients');
                          setSelectedClient(null); 
                          setEditingClient(false); 
                          openBookingDetail(booking); 
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{booking.package?.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-slate-400">{booking.preferred_date} at {booking.preferred_time}</p>
                            <p className="text-xs text-slate-500">{booking.make_model} - {booking.vehicle_type}</p>
                            {booking.payment_records?.length > 0 && (
                              <p className="text-xs text-green-400 mt-1">
                                Paid: {booking.payment_records.map(p => `${p.method}: $${p.amount}`).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-cyan-400 font-bold">${booking.total_price?.toFixed(2)}</p>
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(booking.status)}`}>
                              {formatStatus(booking.status)}
                            </span>
                          </div>
                        </div>
                        {booking.admin_notes && (
                          <div className="mt-2 pt-2 border-t border-slate-700">
                            <p className="text-xs text-slate-400 whitespace-pre-wrap">{booking.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Buttons */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-700">
                <a
                  href={`sms:${selectedClient.phone}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  Text
                </a>
                <a
                  href={`tel:${selectedClient.phone}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </a>
                <a
                  href={`mailto:${selectedClient.email}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Booking Detail Modal */}
      <AnimatePresence>
        {selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedBooking(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {previousView === 'clients' && previousClient && (
                    <button
                      onClick={() => {
                        setSelectedBooking(null);
                        setSelectedClient(previousClient);
                        setPreviousView(null);
                        setPreviousClient(null);
                      }}
                      className="text-slate-400 hover:text-white flex items-center gap-1"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      <span className="text-sm">Back</span>
                    </button>
                  )}
                  <h2 className="text-xl font-bold">Booking Details</h2>
                </div>
                <button onClick={() => { setSelectedBooking(null); setPreviousView(null); setPreviousClient(null); }} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-500 text-xs">Customer</p>
                    <p className="font-medium">{selectedBooking.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Status</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedBooking.status)}`}>
                        {formatStatus(selectedBooking.status)}
                      </span>
                      {!selectedBooking.user_id && (
                        <span className="px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400">
                          Guest Booking
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-500 text-xs">Phone</p>
                    <a href={`tel:${selectedBooking.phone}`} className="text-cyan-400">{selectedBooking.phone}</a>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Email</p>
                    <p className="text-sm">{selectedBooking.email || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-slate-500 text-xs">Preferred Contact</p>
                  <p className="font-medium text-cyan-400">{selectedBooking.preferred_contact === 'email' ? 'Email' : 'Text (SMS)'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-500 text-xs">Service Date</p>
                    <p>{selectedBooking.preferred_date}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Service Time</p>
                    <p>{selectedBooking.preferred_time}</p>
                  </div>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-slate-500 text-xs">Booked On</p>
                  <p className="text-amber-400 font-medium">
                    {selectedBooking.created_at 
                      ? `${new Date(selectedBooking.created_at).toLocaleDateString()} at ${new Date(selectedBooking.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                      : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-slate-500 text-xs">Address</p>
                  <p className="text-sm">{selectedBooking.address}</p>
                  {selectedBooking.car_address && (
                    <p className="text-cyan-400 text-sm mt-1">Car at: {selectedBooking.car_address}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-500 text-xs">Package</p>
                    <p className="font-medium capitalize">{selectedBooking.package?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Vehicle</p>
                    <p>{selectedBooking.make_model} ({selectedBooking.vehicle_type})</p>
                  </div>
                </div>

                {/* Add-ons Display */}
                {selectedBooking.addons?.length > 0 && (
                  <div>
                    <p className="text-slate-500 text-xs">Add-ons</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedBooking.addons.map((addon, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded capitalize">
                          {addon.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual Discount Display */}
                {selectedBooking.manual_discount?.value > 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                    <p className="text-green-400 text-xs font-medium">
                      Manual Discount: {selectedBooking.manual_discount.type === 'percentage' 
                        ? `${selectedBooking.manual_discount.value}%` 
                        : `$${selectedBooking.manual_discount.value}`}
                    </p>
                    {selectedBooking.manual_discount.reason && (
                      <p className="text-xs text-slate-400">Reason: {selectedBooking.manual_discount.reason}</p>
                    )}
                  </div>
                )}

                {selectedBooking.car_condition && (
                  <div>
                    <p className="text-slate-500 text-xs">Car Condition</p>
                    <p className="text-sm">{selectedBooking.car_condition}</p>
                  </div>
                )}

                {selectedBooking.special_instructions && (
                  <div>
                    <p className="text-slate-500 text-xs">Special Instructions</p>
                    <p className="text-sm">{selectedBooking.special_instructions}</p>
                  </div>
                )}

                {/* Price Section with Edit */}
                <div className="bg-slate-800 rounded-lg p-4">
                  <div className="flex justify-between mb-2">
                    <div>
                      <span className="text-slate-400">Package</span>
                      <span className="text-xs text-slate-500 ml-2 capitalize">({selectedBooking.package?.replace(/_/g, ' ')})</span>
                    </div>
                    <span>${selectedBooking.package_price?.toFixed(2)}</span>
                  </div>
                  {selectedBooking.addons_price > 0 && (
                    <div className="flex justify-between mb-2">
                      <div>
                        <span className="text-slate-400">Add-ons</span>
                        <span className="text-xs text-purple-400 ml-2">
                          ({selectedBooking.addons?.map(a => a.replace(/_/g, ' ')).join(', ')})
                        </span>
                      </div>
                      <span>${selectedBooking.addons_price?.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedBooking.travel_fee > 0 && (
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-400">Travel Fee</span>
                      <span>${selectedBooking.travel_fee?.toFixed(2)}</span>
                    </div>
                  )}
                  {(selectedBooking.discount_amount > 0 || selectedBooking.discount_type) && (
                    <div className="flex justify-between mb-2 text-green-400">
                      <div className="flex items-center gap-2">
                        <span>Discount</span>
                        {selectedBooking.discount_type && (
                          <span className="text-xs bg-green-500/20 px-2 py-0.5 rounded-full">
                            {selectedBooking.discount_type}
                          </span>
                        )}
                        {selectedBooking.discount_code && !selectedBooking.discount_type && (
                          <span className="text-xs bg-green-500/20 px-2 py-0.5 rounded-full font-mono">
                            {selectedBooking.discount_code}
                          </span>
                        )}
                      </div>
                      <span>-${(selectedBooking.discount_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t border-slate-700 pt-2">
                    <span className="font-bold">Total</span>
                    {showEditPrice ? (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">$</span>
                        <Input
                          type="number"
                          value={editedPrice}
                          onChange={(e) => setEditedPrice(e.target.value)}
                          className="w-24 bg-slate-700 border-slate-600 text-right"
                        />
                        <Button
                          onClick={() => {
                            handleUpdateBooking(selectedBooking.id, { total_price: parseFloat(editedPrice) });
                            setShowEditPrice(false);
                          }}
                          className="bg-green-600 hover:bg-green-700 px-2 py-1 text-xs"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={() => setShowEditPrice(false)}
                          className="bg-slate-600 hover:bg-slate-500 px-2 py-1 text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-400 font-bold">${selectedBooking.total_price?.toFixed(2)}</span>
                        <button
                          onClick={() => setShowEditPrice(true)}
                          className="text-slate-400 hover:text-cyan-400 text-xs underline"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Before & After Photos Section - Moved up */}
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-4">
                  <p className="text-slate-400 text-sm font-medium">Before & After Photos</p>
                  
                  {/* Customer Photos */}
                  {selectedBooking.customer_photos?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Customer Photos</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.customer_photos.map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={`${BACKEND_URL}${photo}`} 
                              alt={`Customer ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-75"
                              onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                            />
                            <button
                              onClick={() => deletePhoto(photo)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs hidden group-hover:flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Before Photos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-yellow-400">Before Photos</p>
                      <button
                        onClick={() => { setPhotoUploadType("before"); photoInputRef.current?.click(); }}
                        className="text-xs text-cyan-400 hover:underline flex items-center gap-1"
                        disabled={uploadingPhotos}
                      >
                        <Camera className="w-3 h-3" />
                        Upload
                      </button>
                    </div>
                    {selectedBooking.before_photos?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.before_photos.map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={`${BACKEND_URL}${photo}`} 
                              alt={`Before ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-75"
                              onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                            />
                            <button
                              onClick={() => deletePhoto(photo)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs hidden group-hover:flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-600">No before photos uploaded</p>
                    )}
                  </div>

                  {/* After Photos */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-green-400">After Photos</p>
                      <button
                        onClick={() => { setPhotoUploadType("after"); photoInputRef.current?.click(); }}
                        className="text-xs text-cyan-400 hover:underline flex items-center gap-1"
                        disabled={uploadingPhotos}
                      >
                        <Camera className="w-3 h-3" />
                        Upload
                      </button>
                    </div>
                    {selectedBooking.after_photos?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.after_photos.map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={`${BACKEND_URL}${photo}`} 
                              alt={`After ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-75"
                              onClick={() => window.open(`${BACKEND_URL}${photo}`, '_blank')}
                            />
                            <button
                              onClick={() => deletePhoto(photo)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs hidden group-hover:flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-600">No after photos uploaded</p>
                    )}
                  </div>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={photoInputRef}
                    onChange={handleServicePhotoUpload}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  {uploadingPhotos && (
                    <p className="text-xs text-cyan-400">Uploading photos...</p>
                  )}
                </div>

                {/* Admin Notes Section */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-slate-400 text-sm font-medium">Admin Notes</p>
                  </div>
                  <Textarea
                    placeholder="Add notes about service performed, price changes, add-ons, special requests, follow-up actions, etc..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-sm min-h-[100px]"
                    rows={5}
                  />
                  <Button
                    onClick={() => handleUpdateBooking(selectedBooking.id, { admin_notes: adminNotes })}
                    className="mt-2 bg-cyan-600 hover:bg-cyan-500 text-xs"
                  >
                    Save Notes
                  </Button>
                  {selectedBooking.admin_notes && selectedBooking.admin_notes !== adminNotes && (
                    <p className="text-xs text-yellow-400 mt-1">Unsaved changes</p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-700">
                  {/* Deposit Paid Label */}
                  {selectedBooking.status === 'deposit_paid' && (
                    <div className="w-full mb-2 bg-green-500/10 border border-green-500/30 rounded-lg p-2 text-center">
                      <span className="text-green-400 text-sm font-medium">✓ Deposit Paid - Contact customer to confirm time</span>
                    </div>
                  )}
                  
                  {/* Payment Records Display */}
                  {selectedBooking.payment_records?.length > 0 && (
                    <div className="w-full mb-2 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <p className="text-green-400 text-sm font-medium mb-2">Payment Recorded</p>
                      {selectedBooking.payment_records.map((p, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-400 capitalize">{p.method.replace('_', ' ')}</span>
                          <span className="text-white">${p.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t border-green-500/30 mt-2 pt-2 flex justify-between text-sm font-medium">
                        <span className="text-green-400">Total Paid</span>
                        <span className="text-green-400">${selectedBooking.total_paid?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Pending booking actions */}
                  {selectedBooking.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => updateStatus(selectedBooking.id, 'confirmed')}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Confirm Booking
                      </Button>
                      <Button
                        onClick={() => {
                          setPayments([{ method: 'cash', amount: selectedBooking.total_price || 0, notes: '' }]);
                          setShowPaymentModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Record Payment
                      </Button>
                      <Button
                        onClick={() => {
                          // Copy deposit link to clipboard
                          const depositLink = `${selectedBooking.origin_url || window.location.origin}/pay-deposit/${selectedBooking.id}`;
                          navigator.clipboard.writeText(depositLink);
                          toast.success("Deposit link copied to clipboard!");
                        }}
                        className="bg-yellow-600 hover:bg-yellow-700"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Deposit Link
                      </Button>
                    </>
                  )}
                  
                  {selectedBooking.status === 'deposit_paid' && (
                    <Button
                      onClick={() => updateStatus(selectedBooking.id, 'confirmed')}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      Confirm Booking
                    </Button>
                  )}
                  {selectedBooking.status === 'confirmed' && (
                    <Button
                      onClick={() => updateStatus(selectedBooking.id, 'in_progress')}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Start Appointment
                    </Button>
                  )}
                  {selectedBooking.status === 'in_progress' && (
                    <>
                      <Button
                        onClick={() => {
                          setPayments([{ method: 'cash', amount: selectedBooking.total_price - (selectedBooking.deposit_amount || 20), notes: '' }]);
                          setShowPaymentModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Record Payment & Complete
                      </Button>
                      <Button
                        onClick={() => updateStatus(selectedBooking.id, 'completed')}
                        className="bg-slate-600 hover:bg-slate-500"
                      >
                        Complete (No Payment)
                      </Button>
                    </>
                  )}
                  
                  {/* Edit Services Button - available for all non-cancelled bookings */}
                  {selectedBooking.status !== 'cancelled' && (
                    <Button
                      onClick={() => {
                        openBookingForEdit(selectedBooking);
                        setEditingBookingServices(true);
                      }}
                      className="bg-slate-700 hover:bg-slate-600"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit Services
                    </Button>
                  )}
                  
                  {/* Completed booking - can revert or add payment */}
                  {selectedBooking.status === 'completed' && (
                    <>
                      {!selectedBooking.payment_records?.length && (
                        <Button
                          onClick={() => {
                            setPayments([{ method: 'cash', amount: selectedBooking.total_price - (selectedBooking.deposit_amount || 20), notes: '' }]);
                            setShowPaymentModal(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Record Payment
                        </Button>
                      )}
                      <Button
                        onClick={() => updateStatus(selectedBooking.id, 'confirmed')}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        Revert to Confirmed
                      </Button>
                      <Button
                        onClick={() => updateStatus(selectedBooking.id, 'pending_deposit')}
                        className="bg-yellow-600 hover:bg-yellow-700"
                      >
                        Revert to Pending
                      </Button>
                    </>
                  )}
                  
                  {/* Cancelled booking - can reactivate */}
                  {selectedBooking.status === 'cancelled' && (
                    <>
                      <Button
                        onClick={() => updateStatus(selectedBooking.id, 'pending_deposit')}
                        className="bg-yellow-600 hover:bg-yellow-700"
                      >
                        Reactivate (Pending)
                      </Button>
                      <Button
                        onClick={() => updateStatus(selectedBooking.id, 'confirmed')}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        Reactivate (Confirmed)
                      </Button>
                    </>
                  )}
                  
                  {/* Reschedule button - available for all statuses */}
                  <Button
                    onClick={() => {
                      setShowRescheduleModal(true);
                    }}
                    className="bg-slate-600 hover:bg-slate-500"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Change Date/Time
                  </Button>
                  
                  {!['completed', 'cancelled'].includes(selectedBooking.status) && (
                    <Button
                      onClick={() => setShowCancelConfirm(true)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Cancel
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                  
                  <a
                    href={`sms:${selectedBooking.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Text
                  </a>
                  <a
                    href={`tel:${selectedBooking.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    Call
                  </a>
                  
                  {/* Send Invoice Button */}
                  {selectedBooking.email && (
                    <Button
                      onClick={() => handleSendInvoice(selectedBooking.id)}
                      disabled={sendingInvoice}
                      className="bg-emerald-600 hover:bg-emerald-500"
                    >
                      {sendingInvoice ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-1" />
                      )}
                      Send Invoice
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Services Modal */}
      <AnimatePresence>
        {editingBookingServices && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setEditingBookingServices(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Edit Services</h2>
                <button onClick={() => setEditingBookingServices(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm block mb-1">Package</label>
                  <select
                    value={editBookingData.package || ''}
                    onChange={(e) => setEditBookingData({...editBookingData, package: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="xpress_clean">Xpress Clean</option>
                    <option value="deal_xpress">Deal Xpress Detail</option>
                    <option value="full_xpress">Full Xpress Detail</option>
                  </select>
                </div>

                <div>
                  <label className="text-slate-400 text-sm block mb-1">Vehicle Type</label>
                  <select
                    value={editBookingData.vehicle_type || ''}
                    onChange={(e) => setEditBookingData({...editBookingData, vehicle_type: e.target.value})}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="small">Small</option>
                    <option value="suv">SUV</option>
                    <option value="large">Large</option>
                    <option value="xl">XL</option>
                  </select>
                </div>

                <div>
                  <label className="text-slate-400 text-sm block mb-2">Add-ons</label>
                  <div className="space-y-2">
                    {['engine_bay', 'pet_hair', 'headlight_restore', 'odor_removal', 'clay_bar'].map((addon) => (
                      <label key={addon} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editBookingData.addons?.includes(addon)}
                          onChange={(e) => {
                            const newAddons = e.target.checked
                              ? [...(editBookingData.addons || []), addon]
                              : (editBookingData.addons || []).filter(a => a !== addon);
                            setEditBookingData({...editBookingData, addons: newAddons});
                          }}
                          className="rounded bg-slate-700 border-slate-600"
                        />
                        <span className="text-sm capitalize">{addon.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-sm block mb-1">Package Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editBookingData.package_price || 0}
                      onChange={(e) => setEditBookingData({...editBookingData, package_price: parseFloat(e.target.value) || 0})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm block mb-1">Add-ons Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editBookingData.addons_price || 0}
                      onChange={(e) => setEditBookingData({...editBookingData, addons_price: parseFloat(e.target.value) || 0})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm block mb-1">Travel Fee ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editBookingData.travel_fee || 0}
                      onChange={(e) => setEditBookingData({...editBookingData, travel_fee: parseFloat(e.target.value) || 0})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm block mb-1">Subtotal</label>
                    <p className="text-slate-300 font-medium text-lg pt-2">
                      ${((editBookingData.package_price || 0) + (editBookingData.addons_price || 0) + (editBookingData.travel_fee || 0)).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Manual Discount Section */}
                <div className="bg-slate-800/50 rounded-lg p-3 mt-4">
                  <p className="text-slate-400 text-sm font-medium mb-3">Manual Discount</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Type</label>
                      <select
                        value={manualDiscount.type}
                        onChange={(e) => setManualDiscount({...manualDiscount, type: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="dollar">Dollar Amount ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-500 text-xs block mb-1">Value</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={manualDiscount.type === 'percentage' ? '10' : '20.00'}
                        value={manualDiscount.value || ''}
                        onChange={(e) => setManualDiscount({...manualDiscount, value: parseFloat(e.target.value) || 0})}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-slate-500 text-xs block mb-1">Reason for Discount</label>
                    <Input
                      placeholder="e.g., Loyal customer, First time, Referral bonus..."
                      value={manualDiscount.reason}
                      onChange={(e) => setManualDiscount({...manualDiscount, reason: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-sm"
                    />
                  </div>
                  {manualDiscount.value > 0 && (
                    <p className="text-green-400 text-sm mt-2">
                      Discount: -{manualDiscount.type === 'percentage' 
                        ? `${manualDiscount.value}% ($${(((editBookingData.package_price || 0) + (editBookingData.addons_price || 0) + (editBookingData.travel_fee || 0)) * manualDiscount.value / 100).toFixed(2)})`
                        : `$${manualDiscount.value.toFixed(2)}`}
                    </p>
                  )}
                </div>

                {/* Final Total */}
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-cyan-400 font-medium">Final Total</span>
                    <span className="text-cyan-400 font-bold text-xl">
                      ${(() => {
                        const subtotal = (editBookingData.package_price || 0) + (editBookingData.addons_price || 0) + (editBookingData.travel_fee || 0);
                        const discount = manualDiscount.type === 'percentage' 
                          ? subtotal * (manualDiscount.value || 0) / 100 
                          : (manualDiscount.value || 0);
                        return Math.max(0, subtotal - discount).toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleUpdateBookingServices}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => setEditingBookingServices(false)}
                    className="flex-1 bg-slate-600 hover:bg-slate-500"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Recording Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Record Payment</h2>
                <button onClick={() => setShowPaymentModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-slate-800 rounded-lg p-3 mb-4">
                <div className="flex justify-between mb-1">
                  <span className="text-slate-400">Service Total</span>
                  <span>${selectedBooking.total_price?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-slate-400">Deposit Paid</span>
                  <span className="text-green-400">-${(selectedBooking.deposit_amount || 20).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-700 pt-1 font-bold">
                  <span>Remaining Balance</span>
                  <span className="text-cyan-400">${(selectedBooking.total_price - (selectedBooking.deposit_amount || 20)).toFixed(2)}</span>
                </div>
              </div>

              <p className="text-slate-400 text-sm mb-3">Payment Method(s) - supports split payments</p>
              
              <div className="space-y-3 mb-4">
                {payments.map((payment, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                    <div className="flex gap-2 mb-2">
                      <select
                        value={payment.method}
                        onChange={(e) => updatePayment(idx, 'method', e.target.value)}
                        className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Amount"
                        value={payment.amount || ''}
                        onChange={(e) => updatePayment(idx, 'amount', parseFloat(e.target.value) || 0)}
                        className="w-28 bg-slate-800 border-slate-700"
                      />
                      {payments.length > 1 && (
                        <button
                          onClick={() => removePaymentRow(idx)}
                          className="text-red-400 hover:text-red-300 px-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <Input
                      placeholder="Notes (optional)"
                      value={payment.notes || ''}
                      onChange={(e) => updatePayment(idx, 'notes', e.target.value)}
                      className="bg-slate-800 border-slate-700 text-sm"
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={addPaymentRow}
                className="text-cyan-400 hover:underline text-sm mb-4 block"
              >
                + Add split payment
              </button>

              <div className="bg-slate-800 rounded-lg p-3 mb-4">
                <div className="flex justify-between font-bold">
                  <span>Total Recording</span>
                  <span className="text-green-400">${payments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleRecordPayment(true)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Record & Complete
                </Button>
                <Button
                  onClick={() => handleRecordPayment(false)}
                  className="flex-1 bg-slate-600 hover:bg-slate-500"
                >
                  Record Only
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Cancel Confirmation Modal */}
      <AnimatePresence>
        {showCancelConfirm && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowCancelConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Cancel Booking?</h2>
              <p className="text-slate-400 mb-2">
                Customer: <span className="text-white">{selectedBooking.customer_name}</span>
              </p>
              <p className="text-slate-400 mb-6">
                {selectedBooking.preferred_date} at {selectedBooking.preferred_time}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600"
                >
                  No, Keep It
                </Button>
                <Button
                  onClick={() => updateStatus(selectedBooking.id, 'cancelled')}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Yes, Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Delete Booking Permanently?</h2>
              <p className="text-slate-400 mb-2">
                This will completely remove the booking from the system.
              </p>
              <p className="text-slate-400 mb-2">
                Customer: <span className="text-white">{selectedBooking.customer_name}</span>
              </p>
              <p className="text-slate-400 mb-6">
                {selectedBooking.preferred_date} at {selectedBooking.preferred_time}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600"
                >
                  No, Keep It
                </Button>
                <Button
                  onClick={() => deleteBooking(selectedBooking.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Yes, Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Reschedule Modal */}
      <AnimatePresence>
        {showRescheduleModal && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto"
            onClick={() => setShowRescheduleModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-md w-full my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Reschedule Booking</h2>
                <button onClick={() => setShowRescheduleModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-slate-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-400">Customer: <span className="text-white">{selectedBooking.customer_name}</span></p>
                <p className="text-sm text-slate-400">Current: <span className="text-cyan-400">{selectedBooking.preferred_date} at {selectedBooking.preferred_time}</span></p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">New Date</Label>
                  <CalendarComponent
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={setRescheduleDate}
                    disabled={(date) => date < new Date()}
                    className="rounded-lg border border-slate-700 bg-slate-800"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">New Time</Label>
                  <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                      {admin_times.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleAdminReschedule}
                  className="w-full btn-primary-glow text-slate-900"
                >
                  Confirm Reschedule
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
